/**
 * Help Context Configuration
 *
 * Maps application routes to relevant help documentation and suggested questions.
 * Used by the AI Chatbot to provide context-aware help.
 * Suggestion strings use i18n translation keys (help.{section}.{index}).
 *
 * Phase 1.4: Context-Aware Help Button
 *
 * @module help-context.config
 */

import type { SuggestionChip } from '@/features/chatbot/types/chatbot.types';

// ============================================================================
// Page Help Configuration
// ============================================================================

/**
 * Page help configuration interface
 */
export interface PageHelpConfig {
  /** Relative path to relevant documentation */
  docs: string[];
  /** Pre-filled question i18n keys for this page (resolved via help.suggestions namespace) */
  suggestions: string[];
  /** Quick action i18n key and query key pairs */
  quickActions?: {
    label: string;
    query: string;
  }[];
}

/**
 * Bilingual suggestion strings (DRY: single source for EN + TR).
 * Components resolve via resolveSuggestion(key, locale).
 */
export const helpSuggestions: Record<string, { en: string; tr: string }> = {
  'dashboard.customize': {
    en: 'How do I customize my dashboard?',
    tr: 'Panelimi nasıl özelleştiririm?',
  },
  'dashboard.kpi': { en: 'What do the KPI widgets show?', tr: "KPI widget'ları ne gösterir?" },
  'dashboard.dateRange': {
    en: 'How to set date range filter?',
    tr: 'Tarih aralığı filtresi nasıl ayarlanır?',
  },
  'dashboard.guide': {
    en: 'Show me how to use the dashboard',
    tr: 'Paneli nasıl kullanacağımı göster',
  },
  'orders.search': {
    en: 'How to search and filter orders?',
    tr: 'Siparişleri nasıl arar ve filtrelerim?',
  },
  'orders.status': {
    en: 'What do order status meanings?',
    tr: 'Sipariş durumları ne anlama gelir?',
  },
  'orders.export': { en: 'How to export orders?', tr: 'Siparişleri nasıl dışa aktarırım?' },
  'orders.create': { en: 'How do I create a new order?', tr: 'Yeni sipariş nasıl oluştururum?' },
  'orders.requiredFields': {
    en: 'What are the required fields for orders?',
    tr: 'Sipariş için zorunlu alanlar neler?',
  },
  'orders.deliveryAddress': {
    en: 'How to set delivery address?',
    tr: 'Teslimat adresi nasıl belirlenir?',
  },
  'orders.viewDetails': {
    en: 'How to view order details?',
    tr: 'Sipariş detaylarını nasıl görürüm?',
  },
  'orders.edit': { en: 'How to edit an order?', tr: 'Siparişi nasıl düzenlerim?' },
  'orders.cancel': { en: 'How to cancel an order?', tr: 'Siparişi nasıl iptal ederim?' },
  'shipments.track': { en: 'How to track shipments?', tr: 'Sevkiyatları nasıl takip ederim?' },
  'shipments.status': { en: 'Shipment status meanings', tr: 'Sevkiyat durumları' },
  'shipments.createFromOrder': {
    en: 'How to create shipment from order?',
    tr: 'Siparişten sevkiyat nasıl oluşturulur?',
  },
  'shipments.viewDetails': {
    en: 'How to view shipment details?',
    tr: 'Sevkiyat detaylarını nasıl görürüm?',
  },
  'shipments.updateStatus': {
    en: 'How to update shipment status?',
    tr: 'Sevkiyat durumunu nasıl güncellerim?',
  },
  'shipments.assignDriver': {
    en: 'How to assign driver to shipment?',
    tr: 'Sevkiyata sürücü nasıl atarım?',
  },
  'vehicles.add': { en: 'How to add a new vehicle?', tr: 'Yeni araç nasıl eklerim?' },
  'vehicles.maintenance': { en: 'Vehicle maintenance schedules', tr: 'Araç bakım programları' },
  'vehicles.assignDriver': {
    en: 'How to assign driver to vehicle?',
    tr: 'Araca sürücü nasıl atarım?',
  },
  'vehicles.requiredFields': {
    en: 'What fields are required for vehicles?',
    tr: 'Araç için zorunlu alanlar neler?',
  },
  'vehicles.documents': {
    en: 'How to add vehicle documents?',
    tr: 'Araç belgeleri nasıl eklenir?',
  },
  'vehicles.maintenanceReminders': {
    en: 'How to set maintenance reminders?',
    tr: 'Bakım hatırlatıcıları nasıl ayarlanır?',
  },
  'drivers.add': { en: 'How to add a new driver?', tr: 'Yeni sürücü nasıl eklerim?' },
  'drivers.certifications': {
    en: 'Driver certification management',
    tr: 'Sürücü sertifika yönetimi',
  },
  'drivers.assignTrip': { en: 'How to assign driver to trip?', tr: 'Sürücüye sefer nasıl atarım?' },
  'drivers.documents': {
    en: 'What documents are needed for drivers?',
    tr: 'Sürücüler için hangi belgeler gerekli?',
  },
  'drivers.availability': {
    en: 'How to set driver availability?',
    tr: 'Sürücü uygunluğu nasıl ayarlanır?',
  },
  'drivers.addCertifications': {
    en: 'How to add driver certifications?',
    tr: 'Sürücü sertifikaları nasıl eklenir?',
  },
  'routes.create': { en: 'How to create a new route?', tr: 'Yeni rota nasıl oluştururum?' },
  'routes.optimization': {
    en: 'How does route optimization work?',
    tr: 'Rota optimizasyonu nasıl çalışır?',
  },
  'routes.assignDriver': {
    en: 'How to assign route to driver?',
    tr: 'Rotaya sürücü nasıl atarım?',
  },
  'routes.addStops': { en: 'How to add stops to a route?', tr: 'Rotaya durak nasıl eklerim?' },
  'routes.timeWindows': { en: 'What are time windows?', tr: 'Zaman pencereleri nedir?' },
  'routes.optimizeOrder': {
    en: 'How to optimize route order?',
    tr: 'Rota sırası nasıl optimize edilir?',
  },
  'trips.create': { en: 'How to create a new trip?', tr: 'Yeni sefer nasıl oluştururum?' },
  'trips.status': { en: 'Trip status meanings', tr: 'Sefer durumları' },
  'trips.trackProgress': {
    en: 'How to track trip progress?',
    tr: 'Sefer ilerlemesi nasıl takip edilir?',
  },
  'customers.add': { en: 'How to add a new customer?', tr: 'Yeni müşteri nasıl eklerim?' },
  'customers.types': { en: 'Customer types and categories', tr: 'Müşteri türleri ve kategorileri' },
  'customers.addresses': {
    en: 'How to manage customer addresses?',
    tr: 'Müşteri adresleri nasıl yönetilir?',
  },
  'tracking.realtime': {
    en: 'How to view real-time tracking?',
    tr: 'Gerçek zamanlı takip nasıl görülür?',
  },
  'tracking.gpsStatus': { en: 'How to understand GPS status?', tr: 'GPS durumu nasıl anlaşılır?' },
  'tracking.geofence': {
    en: 'How to set geofence alerts?',
    tr: 'Coğrafi çit uyarıları nasıl ayarlanır?',
  },
  'finance.reports': {
    en: 'How to view financial reports?',
    tr: 'Finansal raporları nasıl görürüm?',
  },
  'finance.invoiceStatus': {
    en: 'Understanding invoice statuses',
    tr: 'Fatura durumlarını anlama',
  },
  'finance.export': {
    en: 'How to export financial data?',
    tr: 'Finansal verileri nasıl dışa aktarırım?',
  },
  'reports.create': { en: 'How to create a report?', tr: 'Rapor nasıl oluştururum?' },
  'reports.types': { en: 'Available report types', tr: 'Mevcut rapor türleri' },
  'reports.schedule': { en: 'How to schedule reports?', tr: 'Raporlar nasıl zamanlanır?' },
  'settings.password': { en: 'How to change password?', tr: 'Şifre nasıl değiştirilir?' },
  'settings.notifications': {
    en: 'How to configure notifications?',
    tr: 'Bildirimler nasıl yapılandırılır?',
  },
  'settings.company': { en: 'Company settings explanation', tr: 'Şirket ayarları açıklaması' },
  'admin.addUser': { en: 'How to add a new user?', tr: 'Yeni kullanıcı nasıl eklenir?' },
  'admin.roles': { en: 'User roles and permissions', tr: 'Kullanıcı rolleri ve izinler' },
  'admin.resetPassword': {
    en: 'How to reset user password?',
    tr: 'Kullanıcı şifresi nasıl sıfırlanır?',
  },
  'default.createOrder': {
    en: 'How do I create a new order?',
    tr: 'Yeni sipariş nasıl oluştururum?',
  },
  'default.trackShipment': {
    en: 'How do I track a shipment?',
    tr: 'Sevkiyatı nasıl takip ederim?',
  },
  'default.addDriver': { en: 'How do I add a new driver?', tr: 'Yeni sürücü nasıl eklerim?' },
  'default.generateReport': { en: 'How do I generate a report?', tr: 'Rapor nasıl oluştururum?' },
};

/**
 * Resolve suggestion key to locale-aware text.
 * Single source of truth — replaces the previous two parallel dicts.
 */
export function resolveSuggestion(key: string, locale: string = 'en'): string {
  const entry = helpSuggestions[key];
  if (!entry) return key;
  return locale === 'tr' ? entry.tr : entry.en;
}

export const pageHelpConfig: Record<string, PageHelpConfig> = {
  // ========================================================================
  // Dashboard
  // ========================================================================
  '/': {
    docs: ['/docs/user-guides/fundamentals/dashboard/'],
    suggestions: ['dashboard.customize', 'dashboard.kpi', 'dashboard.dateRange'],
    quickActions: [{ label: 'dashboard.guide', query: 'dashboard.guide' }],
  },
  '/dashboard': {
    docs: ['/docs/user-guides/fundamentals/dashboard/'],
    suggestions: ['dashboard.customize', 'dashboard.kpi', 'dashboard.dateRange'],
  },

  // ========================================================================
  // Orders
  // ========================================================================
  '/orders': {
    docs: ['/docs/user-guides/operations/orders/'],
    suggestions: ['orders.search', 'orders.status', 'orders.export'],
  },
  '/orders/new': {
    docs: ['/docs/user-guides/operations/orders/'],
    suggestions: ['orders.create', 'orders.requiredFields', 'orders.deliveryAddress'],
  },
  '/orders/[id]': {
    docs: ['/docs/user-guides/operations/orders/'],
    suggestions: ['orders.viewDetails', 'orders.edit', 'orders.cancel'],
  },

  // ========================================================================
  // Shipments
  // ========================================================================
  '/shipments': {
    docs: ['/docs/user-guides/operations/shipments/'],
    suggestions: ['shipments.track', 'shipments.status', 'shipments.createFromOrder'],
  },
  '/shipments/[id]': {
    docs: ['/docs/user-guides/operations/shipments/'],
    suggestions: ['shipments.viewDetails', 'shipments.updateStatus', 'shipments.assignDriver'],
  },

  // ========================================================================
  // Vehicles
  // ========================================================================
  '/vehicles': {
    docs: ['/docs/user-guides/operations/vehicles/'],
    suggestions: ['vehicles.add', 'vehicles.maintenance', 'vehicles.assignDriver'],
  },
  '/vehicles/new': {
    docs: ['/docs/user-guides/operations/vehicles/'],
    suggestions: ['vehicles.requiredFields', 'vehicles.documents', 'vehicles.maintenanceReminders'],
  },

  // ========================================================================
  // Drivers
  // ========================================================================
  '/drivers': {
    docs: ['/docs/user-guides/operations/drivers/'],
    suggestions: ['drivers.add', 'drivers.certifications', 'drivers.assignTrip'],
  },
  '/drivers/new': {
    docs: ['/docs/user-guides/operations/drivers/'],
    suggestions: ['drivers.documents', 'drivers.availability', 'drivers.addCertifications'],
  },

  // ========================================================================
  // Routes
  // ========================================================================
  '/routes': {
    docs: ['/docs/user-guides/operations/routes/'],
    suggestions: ['routes.create', 'routes.optimization', 'routes.assignDriver'],
  },
  '/routes/new': {
    docs: ['/docs/user-guides/operations/routes/'],
    suggestions: ['routes.addStops', 'routes.timeWindows', 'routes.optimizeOrder'],
  },

  // ========================================================================
  // Trips
  // ========================================================================
  '/trips': {
    docs: ['/docs/user-guides/operations/trips/'],
    suggestions: ['trips.create', 'trips.status', 'trips.trackProgress'],
  },

  // ========================================================================
  // Customers
  // ========================================================================
  '/customers': {
    docs: ['/docs/user-guides/operations/customers/'],
    suggestions: ['customers.add', 'customers.types', 'customers.addresses'],
  },
  '/customers/[id]': {
    docs: ['/docs/user-guides/operations/customers/'],
    suggestions: ['customers.add', 'customers.types', 'customers.addresses'],
  },

  // ========================================================================
  // Tracking / GPS
  // ========================================================================
  '/tracking': {
    docs: ['/docs/user-guides/operations/tracking-gps/'],
    suggestions: ['tracking.realtime', 'tracking.gpsStatus', 'tracking.geofence'],
  },

  // ========================================================================
  // Maps
  // ========================================================================
  '/maps': {
    docs: ['/docs/user-guides/operations/maps/'],
    suggestions: ['maps.use', 'maps.search', 'maps.zones'],
  },

  // ========================================================================
  // Zones
  // ========================================================================
  '/zones': {
    docs: ['/docs/user-guides/operations/zones/'],
    suggestions: ['zones.create', 'zones.types', 'zones.analytics'],
  },

  // ========================================================================
  // Finance
  // ========================================================================
  '/finance': {
    docs: ['/docs/user-guides/finance/'],
    suggestions: ['finance.reports', 'finance.invoiceStatus', 'finance.export'],
  },
  '/finance/invoices': {
    docs: ['/docs/user-guides/finance/invoicing/'],
    suggestions: ['finance.createInvoice', 'finance.invoiceStatus', 'finance.sendInvoice'],
  },
  '/finance/carrier-rates': {
    docs: ['/docs/user-guides/finance/carrier-rates/'],
    suggestions: ['finance.addRates', 'finance.rateTypes', 'finance.compareRates'],
  },
  '/finance/subscriptions': {
    docs: ['/docs/user-guides/finance/subscriptions/'],
    suggestions: ['finance.manageSubscriptions', 'finance.planTypes', 'finance.upgrade'],
  },

  // ========================================================================
  // Reports
  // ========================================================================
  '/reports': {
    docs: ['/docs/user-guides/intelligence/reporting/'],
    suggestions: ['reports.create', 'reports.types', 'reports.schedule'],
  },

  // ========================================================================
  // AI Features
  // ========================================================================
  '/ai-routing': {
    docs: ['/docs/user-guides/intelligence/ai-routing/'],
    suggestions: ['ai.routingHowItWorks', 'ai.optimizeRoutes', 'ai.routingSettings'],
  },
  '/ai-chatbot': {
    docs: ['/docs/user-guides/intelligence/ai-chatbot/'],
    suggestions: ['ai.useAssistant', 'ai.whatToAsk', 'ai.betterResponses'],
  },

  // ========================================================================
  // Settings
  // ========================================================================
  '/settings': {
    docs: ['/docs/user-guides/fundamentals/settings/'],
    suggestions: ['settings.password', 'settings.notifications', 'settings.company'],
  },
  '/settings/profile': {
    docs: ['/docs/user-guides/fundamentals/settings/'],
    suggestions: ['settings.updateProfile', 'settings.language', 'settings.timezone'],
  },
  '/settings/notifications': {
    docs: ['/docs/user-guides/operations/notifications/'],
    suggestions: [
      'settings.notificationPreferences',
      'settings.notificationTypes',
      'settings.quietHours',
    ],
  },

  // ========================================================================
  // Administration
  // ========================================================================
  '/admin/users': {
    docs: ['/docs/user-guides/administration/users/'],
    suggestions: ['admin.addUser', 'admin.roles', 'admin.resetPassword'],
  },
  '/admin/tenants': {
    docs: ['/docs/user-guides/administration/tenants/'],
    suggestions: ['admin.tenantSettings', 'admin.tenantConfig', 'admin.tenantUsage'],
  },
  '/admin/integrations': {
    docs: ['/docs/user-guides/administration/integrations/'],
    suggestions: ['admin.addIntegration', 'admin.availableIntegrations', 'admin.configureApiKeys'],
  },
  '/admin/audit-logs': {
    docs: ['/docs/user-guides/administration/audit-logs/'],
    suggestions: ['admin.viewAuditLogs', 'admin.loggedEvents', 'admin.exportAuditLogs'],
  },

  // ========================================================================
  // Data Import
  // ========================================================================
  '/data-import': {
    docs: ['/docs/user-guides/operations/data-import/'],
    suggestions: ['dataImport.howTo', 'dataImport.formats', 'dataImport.mapColumns'],
  },

  // ========================================================================
  // Grid Views
  // ========================================================================
  '/grid-views': {
    docs: ['/docs/user-guides/operations/grid-views/'],
    suggestions: ['gridViews.save', 'gridViews.customizeColumns', 'gridViews.filters'],
  },

  // ========================================================================
  // Saved Addresses
  // ========================================================================
  '/saved-addresses': {
    docs: ['/docs/user-guides/operations/saved-addresses/'],
    suggestions: ['savedAddresses.save', 'savedAddresses.groups', 'savedAddresses.import'],
  },

  // ========================================================================
  // Documents / POD
  // ========================================================================
  '/documents': {
    docs: ['/docs/user-guides/operations/documents-pod/'],
    suggestions: ['documents.upload', 'documents.types', 'documents.viewPod'],
  },

  // ========================================================================
  // Onboarding
  // ========================================================================
  '/onboarding': {
    docs: ['/docs/user-guides/operations/onboarding/'],
    suggestions: ['onboarding.complete', 'onboarding.requiredSettings', 'onboarding.inviteTeam'],
  },

  // ========================================================================
  // Contact
  // ========================================================================
  '/contact': {
    docs: ['/docs/user-guides/operations/contact/'],
    suggestions: ['contact.add', 'contact.types', 'contact.import'],
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get help config for a given path
 *
 * @param path - Current route path
 * @returns Page help config or undefined if not found
 */
export function getPageHelpConfig(path: string): PageHelpConfig | undefined {
  // Exact match first
  if (pageHelpConfig[path]) {
    return pageHelpConfig[path];
  }

  // Try pattern matching for dynamic routes
  const segments = path.split('/').filter(Boolean);

  for (const configPath of Object.keys(pageHelpConfig)) {
    const configSegments = configPath.split('/').filter(Boolean);

    // Skip if segment count doesn't match
    if (configSegments.length !== segments.length) {
      continue;
    }

    // Check if paths match with wildcards
    let matches = true;
    for (let i = 0; i < configSegments.length; i++) {
      const configSegment = configSegments[i];
      const pathSegment = segments[i];

      // Skip dynamic segments like [id]
      if (configSegment.startsWith('[') && configSegment.endsWith(']')) {
        continue;
      }

      if (configSegment !== pathSegment) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return pageHelpConfig[configPath];
    }
  }

  return undefined;
}

/**
 * Get default suggestions when no page-specific help is available
 */
export function getDefaultSuggestions(locale: string = 'en'): SuggestionChip[] {
  return [
    {
      id: 'create-order',
      label: resolveSuggestion('default.createOrder', locale),
      query: resolveSuggestion('default.createOrder', locale),
    },
    {
      id: 'track-shipment',
      label: resolveSuggestion('default.trackShipment', locale),
      query: resolveSuggestion('default.trackShipment', locale),
    },
    {
      id: 'add-driver',
      label: resolveSuggestion('default.addDriver', locale),
      query: resolveSuggestion('default.addDriver', locale),
    },
    {
      id: 'generate-report',
      label: resolveSuggestion('default.generateReport', locale),
      query: resolveSuggestion('default.generateReport', locale),
    },
  ];
}

// ============================================================================
// Export all configs
// ============================================================================

export default pageHelpConfig;
